import { nextDaPaginaAtual, urlLogin } from "@/lib/caminhoInterno";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "";
const REQUEST_TIMEOUT_MS = 60000;

function getToken(): string | undefined {
  if (typeof document === "undefined") return undefined;
  return document.cookie
    .split("; ")
    .find((c) => c.startsWith("token="))
    ?.split("=")[1];
}

function setToken(token: string, maxAge = 30 * 24 * 60 * 60) {
  document.cookie = `token=${token}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function clearToken() {
  document.cookie = "token=; path=/; max-age=0";
}

function unwrapApiJson(body: unknown): unknown {
  if (body === null || typeof body !== "object" || Array.isArray(body)) {
    return body;
  }
  const o = body as Record<string, unknown>;
  if (!("_sql_queries" in o)) {
    return body;
  }
  const { _sql_queries: _q, ...rest } = o;
  const keys = Object.keys(rest);
  if (keys.length === 1 && keys[0] === "data") {
    return rest.data;
  }
  if (keys.length === 1 && keys[0] === "result") {
    return rest.result;
  }
  return rest;
}

/** Mensagem do corpo de erro da API: `message`/`detail` (lista do Pydantic → 1ª `msg`). */
function mensagemDoCorpo(body: unknown, padrao: string): string {
  const b = (body ?? {}) as { message?: unknown; detail?: unknown };
  const raw = b.message || b.detail;
  if (typeof raw === "string" && raw) return raw;
  if (Array.isArray(raw) && raw[0] && typeof raw[0].msg === "string") return raw[0].msg;
  return padrao;
}

/**
 * Erro HTTP do `request`: um `Error` comum (mesma mensagem de sempre) com o `status` e o
 * corpo (`body`) pendurados — quem precisa (409 de conflito, `caminho` do 422, 410) lê sem
 * regex no texto; o resto do app continua usando só `message`.
 */
export type ErroApi = Error & { status: number; body: unknown };

async function erroHttp(res: Response, padrao: string): Promise<ErroApi> {
  const body: unknown = await res.json().catch(() => ({}));
  return Object.assign(new Error(mensagemDoCorpo(body, padrao)), { status: res.status, body });
}

/** 401: limpa a sessão e, se havia uma, vai ao login voltando a esta página (`?next=`). */
function sessaoExpirou(tinhaToken: boolean) {
  clearToken();
  if (tinhaToken && typeof window !== "undefined") window.location.href = urlLogin(nextDaPaginaAtual());
}

export async function request<T>(path: string, options?: RequestInit, timeoutMs?: number): Promise<T> {
  const token = getToken();
  // Upload (FormData) NÃO pode levar Content-Type fixo: quem monta o cabeçalho
  // multipart é o browser, porque só ele conhece o `boundary`. Forçar
  // application/json aqui faz o backend receber um corpo que não sabe parsear.
  const ehUpload = options?.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(ehUpload ? {} : { "Content-Type": "application/json" }),
    ...(options?.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  // 60s serve quase toda chamada; endpoints sabidamente longos (ex.: envio ao ERP com várias
  // filiais) passam `timeoutMs` maior — abortar no cliente não cancela o servidor.
  const timeout = timeoutMs ?? REQUEST_TIMEOUT_MS;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // Permite cancelar externamente (ex.: debounce de busca): combina o signal do
  // chamador com o controller interno (que também cuida do timeout).
  const external = options?.signal as AbortSignal | undefined;
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener("abort", () => controller.abort(), { once: true });
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof Error) {
      if (err.name === "AbortError") {
        // Cancelamento explícito pelo chamador: propaga como AbortError p/ ser ignorado.
        if (external?.aborted) throw err;
        throw new Error("Servidor demorou para responder. Verifique sua conexão e a URL da API.");
      }
      if (err.message.includes("Failed to fetch") || err.message.includes("NetworkError")) {
        throw new Error("Não foi possível conectar ao servidor. Verifique a URL da API e CORS.");
      }
    }
    throw err;
  }
  clearTimeout(timeoutId);

  const newToken = res.headers.get("X-New-Token");
  if (newToken) setToken(newToken);

  // 401 = sessão inválida/expirada → desloga e volta pro login (e depois para esta página).
  if (res.status === 401) {
    sessaoExpirou(!!token);
    throw await erroHttp(res, "Sessão expirada");
  }
  // 403 = autenticado, mas sem permissão para ESTE recurso: não desloga, a página trata.
  if (res.status === 403) throw await erroHttp(res, "Você não tem permissão para acessar este recurso");
  if (!res.ok) throw await erroHttp(res, `Erro ${res.status}`);

  if (res.status === 204) return undefined as T;
  const body = await res.json();
  return unwrapApiJson(body) as T;
}

/**
 * Igual ao `request`, mas devolve um Blob (download de arquivo, ex.: Excel).
 * Mantém auth/token/erros; lê o nome do arquivo do Content-Disposition.
 * Timeout maior porque a geração server-side pode levar alguns segundos.
 */
export async function requestBlob(
  path: string,
  options?: RequestInit,
): Promise<{ blob: Blob; filename: string }> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options?.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 120000);
  const external = options?.signal as AbortSignal | undefined;
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener("abort", () => controller.abort(), { once: true });
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...options, headers, signal: controller.signal });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof Error && err.name === "AbortError" && !external?.aborted) {
      throw new Error("Servidor demorou para gerar o arquivo.");
    }
    throw err;
  }
  clearTimeout(timeoutId);

  const newToken = res.headers.get("X-New-Token");
  if (newToken) setToken(newToken);

  if (!res.ok) {
    if (res.status === 401) sessaoExpirou(!!token);
    throw await erroHttp(res, `Erro ${res.status}`);
  }

  const cd = res.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="?([^"]+)"?/);
  const filename = m ? decodeURIComponent(m[1]) : "relatorio.xlsx";
  const blob = await res.blob();
  return { blob, filename };
}

/** Upload multipart/form-data (não define Content-Type — o browser monta o boundary). */
export async function requestFormData<T>(path: string, formData: FormData): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { method: "POST", headers, body: formData });
  const newToken = res.headers.get("X-New-Token");
  if (newToken) setToken(newToken);

  if (!res.ok) throw await erroHttp(res, `Erro ${res.status}`);
  const body = await res.json();
  return unwrapApiJson(body) as T;
}
